import { NEWS_LIST_PATH } from '@/mixins/news'
import { readTiptapDocument } from '@components/Tiptap/document'
import qs from 'qs'

export default {
	data() {
		return {
			pagina: {
				translations: []
			},
			// `{ notFound, offline }` quando o carregamento falha; `null` caso contrário
			loadError: null,
			// Identifica a requisição mais recente, para descartar respostas atrasadas
			requestId: 0,
		}
	},
	computed: {
		isValid() {
			return this.pagina != null && this.pagina.id
		},
		blocks() {
			if(this.isValid && this.translation)
				return this.translation.content
			return []
		},
		// Documento do editor novo; null quando vale o `content` legado
		tiptapDocument() {
			if (!this.isValid)
				return null
			return readTiptapDocument(this.translation.contentTiptap)
		},
		translation() {
			if (!this.isValid)
				return {}
			const translation = this.pagina.translations.find(translation => translation.locale == this.$store.state.locale)
			return translation || {}
		},
		tags() {
			if(this.isValid && this.translation.tags)
				return this.translation.tags.map(tag => ({
					name: tag.name,
					content: tag.value,
					vmid: tag.name
				}))
			return []
		},
		navigation() {
			if(this.isValid)
				return this.$store.getters['menus/pagina']
			return {}
		},
		hasNavigation() {
			return this.pagina.template == "pagewithmenu" && this.navigation.id
		},
		isNoticia() {
			return this.$route.name == 'Noticia'
		},
		// Rota coringa: endereço que não corresponde a nenhuma rota
		isNotFoundRoute() {
			return this.$route.name == 'NotFound'
		},
		isHome() {
			return this.$route.name == 'Home'
		},
		isTrabalhe() {
			return this.$route.name == 'Trabalhe'
		},
		isIndustrias() {
			return this.$route.name == 'Industrias'
		},
		isPrecoDia() {
			return this.$route.name == 'Preço'
		},
		// A listagem de notícias tem layout próprio, sem título padrão nem compartilhamento
		isNewsList() {
			return Array.isArray(this.blocks) && this.blocks.some(block => block.type == 'noticias')
		},
		// Textos do erro: notícia volta para a listagem; demais páginas, para a Home
		error() {
			if (!this.loadError)
				return null
			const subject = this.isNoticia ? `a notícia` : `a página`
			const to = this.isNoticia
				? `/${this.$store.state.locale}/${NEWS_LIST_PATH}`
				: (this.isHome ? null : `/`)
			const linkLabel = this.isNoticia ? `Ver todas as notícias` : `Ir para a página inicial`
			// Na Home não há para onde voltar: "Tentar novamente" fica sempre disponível
			const retryable = !this.loadError.notFound || this.isHome

			if (this.loadError.notFound)
				return {
					title: this.isNoticia ? `Notícia não encontrada` : `Página não encontrada`,
					message: `O endereço pode estar incorreto ou ${subject} pode ter sido removida.`,
					retryable,
					to,
					linkLabel,
				}
			return {
				title: `Não foi possível carregar ${subject}`,
				// Sem resposta da API é falha de rede; com resposta, o problema é do servidor
				message: this.loadError.offline
					? `Verifique sua conexão e tente novamente.`
					: `Tente novamente em alguns instantes.`,
				retryable,
				to,
				linkLabel,
			}
		},
	},
	watch: {
		pagina: function() {
			if (this.isValid) {
				if (this.isHome || this.isTrabalhe || this.isNoticia || this.isIndustrias) return
				if (this.pagina.category?.menu)
					this.$store.dispatch('menus/getPagina', this.pagina.category.menu.id)
				else if (this.pagina.category.category.menu)
					this.$store.dispatch('menus/getPagina', this.pagina.category.category.menu.id)
			}
		}
	},
	metaInfo() {
		// Sem SSR não há status 404 real: o `noindex` evita que a página de erro seja indexada
		if (this.loadError)
			return {
				title: this.error.title,
				meta: [ { name: 'robots', content: 'noindex', vmid: 'robots' } ]
			}
		return {
			title: this.translation.title,
			meta: this.tags
		}
	},
	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.loadPagina(to.params)
		})
	},
	beforeRouteUpdate(to, from, next) {
		// Mudança só de query (ex.: filtros e paginação da listagem) não recarrega a página
		if (to.path != from.path)
			this.loadPagina(to.params)
		next()
	},
	methods: {
		loadPagina({ category, subcategory, slug, locale = 'pt-br' }) {
			let params = { category }
			const prefix = this.isNoticia ? `news` : `pages`
			if (this.isPrecoDia)
				return
			const requestId = ++this.requestId
			this.loadError = null
			// A instância é reaproveitada entre rotas: sem limpar, a página anterior
			// aparece no layout da próxima até a resposta chegar
			this.pagina = { translations: [] }
			if (this.isNotFoundRoute) {
				this.loadError = { notFound: true }
				return
			}
			if (slug)
				params['sub-category'] = subcategory
			else
				slug = subcategory
			if (locale)
				this.$store.state.locale = locale
			if (this.isHome) {
				params = { category: 'home' }
				slug = 'pagina-inicial'
			}
			if (this.isTrabalhe) {
				params = { category: 'trabalhe-conosco' }
				slug = 'trabalhe-conosco'
			}
			if (this.isIndustrias) {
				params = { category: 'industrias' }
				slug = 'industrias'
			}
			this.$axios
				.get(`${prefix}/${this.$store.state.locale}/${slug}?${qs.stringify(params)}`)
				.then(response => {
					if (requestId != this.requestId)
						return
					const pagina = this.isNoticia ? response.data.news : response.data.page
					// Sem dado ou sem a tradução do idioma atual, a tela ficaria no esqueleto para sempre
					const locale = this.$store.state.locale
					if (!pagina || !(pagina.translations || []).some(translation => translation.locale == locale)) {
						this.loadError = { notFound: true }
						return
					}
					this.pagina = pagina
				})
				.catch(error => {
					if (requestId != this.requestId)
						return
					// A API responde 400 para página/notícia inexistente (404 seria o esperado)
					const status = error.response?.status
					this.loadError = { notFound: [400, 404].includes(status), offline: !error.response }
				})
		},
		parseNavigation(item) {
			console.log(item)
			// if (item.parent)
			// 	return this.parseNavigation(item.parent)
			// return this.parseChildren(item)
		},
		parseChildren(item) {
			return item.children.map(child => {
				return {
					external: false,
					title: child.title,
					children: child.children ? this.parseChildren(child || {}) : [],
					link: {
						name: `Pagina`,
						params: {
							grandSlug: this.$route.params.grandSlug,
							parentSlug: item.slug,
							slugSon: child.slug
						}
					}
			}})
		},
	},
}
